import React from "react"
import {
    Box,
    IconButton,
    Tooltip,
} from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import VisibilityIcon from "@mui/icons-material/Visibility"

import ButtonWithIcon from "src/v2/components/Button/Button"
import { normalizeExternalHttpUrl } from "src/v2/utils/normalize-href"
import { formatCpfCnpj } from "src/v2/utils/formatter/format-cpf-cnpj"
import { formatHistoryValue } from "./company-history-config"
import theme from "src/v2/theme/theme"

const INLINE_EDITING_SX = {
    "& .MuiDataGrid-cell--editing": {
        backgroundColor:
            theme.palette.input.dynamicTextarea.background.default,
        border: `1px solid ${theme.palette.input.borderColor.primary}`,
    },
    "& .MuiDataGrid-cell--editing .MuiInputBase-input": {
        color: theme.palette.common.white,
        WebkitTextFillColor: theme.palette.common.white,
    },
}

const HISTORY_TABLE_SX = {
    "& .MuiDataGrid-columnHeaderTitle": {
        fontSize: "14px",
        lineHeight: "20px",
    },
}

const getCompanyFieldsSection = (company) => ({
    type: "fields",
    fields: [
        {
            label: "RAZÃO SOCIAL",
            value: company?.RazaoSocial ?? "-",
            md: 3,
        },
        {
            label: "CNPJ",
            value: formatCpfCnpj(company?.CPFCNPJ) || "-",
            md: 3,
        },
        {
            label: "SETOR",
            value: company?.Setor ?? "-",
            md: 3,
        },
        {
            label: "SUBSETOR",
            value: company?.SubSetor ?? "-",
            md: 3,
        },
    ],
})

const getLinkColumn = (column) => ({
    ...column,

    // Impede o tratamento automático de link do DetailsModal,
    // pois este histórico exibe somente o ícone.
    type: undefined,

    renderCell: ({ value }) => {
        const normalizedHref =
            normalizeExternalHttpUrl(value)

        if (!normalizedHref) {
            return "-"
        }

        return (
            <IconButton
                aria-label="Abrir documento do fato relevante"
                component="a"
                href={normalizedHref}
                target="_blank"
                rel="noopener noreferrer"
            >
                <OpenInNewIcon
                    sx={{
                        width: 20,
                        height: 20,
                        fontSize: "20px !important",
                        color: `${theme.palette.icon.main} !important`,
                    }}
                />
            </IconButton>
        )
    },
})

const getBaseColumns = ({
    definition,
    isAdminUser,
    canEdit,
    usesInlineEditing,
    editableFieldNames,
}) =>
    definition.columns
        .filter(
            (column) =>
                !column.adminOnly || isAdminUser
        )
        .map((column) => {
            if (column.type === "link") {
                return getLinkColumn(column)
            }

            return {
                ...column,

                type:
                    column.type === "date"
                        ? "string"
                        : column.type,

                editable: Boolean(
                    canEdit &&
                    usesInlineEditing &&
                    editableFieldNames.has(column.field)
                ),

                minWidth: column.minWidth ?? 150,

                flex:
                    column.flex ??
                    (column.minWidth ? 1.5 : 1),

                formatter:
                    column.formatter ??
                    ((value) =>
                        formatHistoryValue(
                            value,
                            column.type
                        )),
            }
        })

const getViewColumn = ({
    definition,
    handleView,
}) => {
    if (!definition.view) {
        return []
    }

    return [
        {
            field: "visualizar",
            headerName: "Ações",
            width: 160,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,

            renderCell: ({ row }) => (
                <ButtonWithIcon
                    icon={
                        <VisibilityIcon fontSize="small" />
                    }
                    aria-label={`Visualizar ${definition.title}`}
                    onClick={() => handleView(row)}
                >
                    VISUALIZAR
                </ButtonWithIcon>
            ),
        },
    ]
}

const getDraftActionsRenderer = ({
    isDraftRow,
    loading,
    handleSaveDraft,
    handleDiscardDraft,
}) => {
    return function renderDraftActions(row) {
        if (!isDraftRow(row)) {
            return undefined
        }

        return (
            <Box
                sx={{
                    display: "flex",
                    gap: 0.5,
                }}
            >
                <Tooltip title="Salvar novo registro">
                    <IconButton
                        aria-label="Salvar novo registro"
                        onClick={() =>
                            handleSaveDraft(row)
                        }
                        disabled={loading}
                        sx={{
                            color:
                                theme.palette.icon.main,
                        }}
                    >
                        <CheckIcon
                            fontSize="small"
                            sx={{
                                color: `${theme.palette.icon.main} !important`,
                                "&& path": {
                                    fill: "currentColor !important",
                                    stroke: "none !important",
                                },
                            }}
                        />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Descartar novo registro">
                    <IconButton
                        aria-label="Descartar novo registro"
                        onClick={
                            handleDiscardDraft
                        }
                        disabled={loading}
                        sx={{
                            color:
                                theme.palette.icon.main,
                        }}
                    >
                        <CloseIcon
                            fontSize="small"
                            sx={{
                                color: `${theme.palette.icon.main} !important`,
                                "&& path": {
                                    fill: "currentColor !important",
                                    stroke: "none !important",
                                },
                            }}
                        />
                    </IconButton>
                </Tooltip>
            </Box>
        )
    }
}

export const getCompanyHistoryModalConfig = ({
    definition,
    company,
    rows,
    loading,
    tableLoading,
    status,
    error,
    isAdminUser,
    canEdit,
    usesInlineEditing,
    editableFieldNames,
    hasDraftRow,
    isDraftRow,
    isCellEditable,
    handleAdd,
    handleSaveDraft,
    handleDiscardDraft,
    handleDelete,
    handleView,
    handleInlineRowUpdate,
    handleRetry,
}) => {
    const baseColumns = getBaseColumns({
        definition,
        isAdminUser,
        canEdit,
        usesInlineEditing,
        editableFieldNames,
    })

    const viewColumn = getViewColumn({
        definition,
        handleView,
    })

    const columns = [
        ...baseColumns,
        ...viewColumn,
    ]

    const companyFieldsSection =
        getCompanyFieldsSection(company)

    const historyTableSection = {
        type: definition.editable
            ? "edit-table"
            : "table",

        title: "",

        btnAddTitle:
            definition.editable?.interaction?.addLabel ??
            "ADICIONAR NOVO",

        columns,
        rows,
        loading: tableLoading,

        rowHeight: 56,
        contained: false,
        showPagination: true,

        editMode: usesInlineEditing
            ? "cell"
            : undefined,

        isCellEditable: usesInlineEditing
            ? isCellEditable
            : undefined,

        processRowUpdate: usesInlineEditing
            ? handleInlineRowUpdate
            : undefined,

        getRowId: (row) => row.id ?? row.ID,

        actions: {
            addRow: handleAdd,
            deleteRow: handleDelete,
        },

        disableAddRow: !canEdit,

        addRowDisabled:
            loading ||
            status === "error" ||
            hasDraftRow,

        disableDeleteRow: !canEdit,

        renderActions: getDraftActionsRenderer({
            isDraftRow,
            loading,
            handleSaveDraft,
            handleDiscardDraft,
        }),

        empty:
            status === "empty" &&
            !hasDraftRow,

        emptyText: definition.emptyText,

        error: status === "error",
        errorText:
            error ??
            "Não foi possível carregar o histórico.",

        onRetry: handleRetry,

        tableSx: {
            ...HISTORY_TABLE_SX,
            ...(usesInlineEditing
                ? INLINE_EDITING_SX
                : {}),
        },
    }

    return {
        title: definition.title,

        // O vazio é tratado apenas na seção da tabela.
        // Assim, os dados da empresa e o botão de
        // adicionar continuam visíveis.
        empty: false,
        emptyText: "",

        sections: [
            companyFieldsSection,
            historyTableSection,
        ],
    }
}